import { ConversationItem } from "@/store/documentChat";
import { AiChatMessage, AiChatMessageType } from "@/graphql/__generated__/graphql";
import { NavigateFunction } from "react-router-dom";
import { Source } from "./Components/SourceCard";

/**
 * Extracts the file name from a given document URL.
 *
 * @param url - The URL of the document.
 * @returns {string} The file name extracted from the URL, with any leading UUID or hash removed.
 */
export const getFileNameFromDocUrl = (url: string): string => {
  const parts = url.split("/");
  const lastPart = parts[parts.length - 1];
  const fileName = lastPart.replace(/^[a-f0-9-]+-/, "");
  return fileName;
};

/**
 * Parses a raw message string to extract the answer and sources.
 *
 * The message is expected to contain an answer part and a source part
 * separated by the "<SOURCE>" tag. The source part is expected to be a JSON
 * string enclosed within "<SOURCE>" and "</SOURCE>" tags.
 *
 * @param {string} message - The raw message string to be parsed.
 * @returns {{ answer: string, sources: any[] }} An object containing the answer (as a trimmed string) and the sources (as an array).
 */
export const rawMessageToDisplay = (message: string): { answer: string; sources: any[]; } => {
  // Split the message at the "<SOURCE>" tag
  const [answerPart, sourcePartWithClosingTag] = message.split("<SOURCE>");

  // Remove the closing "</SOURCE>" tag and trim whitespace
  const sourcePart = sourcePartWithClosingTag
    ? sourcePartWithClosingTag.replace("</SOURCE>", "").trim()
    : null;

  // Initialize sources array
  let sources = [];

  // Parse the sources if the sourcePart exists
  if (sourcePart) {
    try {
      sources = JSON.parse(sourcePart);
    } catch (e) {
      console.error("Failed to parse sources JSON:", e);
    }
  }

  // Return the cleaned answer and sources
  return {
    answer: answerPart, // The cleaned markdown-formatted answer
    sources, // The parsed array of sources
  };
};

/**
 * Handles click events on the component.
 *
 * If the click is outside of a source element, unnecessary URL parameters are cleared
 * while preserving essential ones like file, page, qaSet and bbox parameters.
 *
 * @param {React.MouseEvent<HTMLDivElement>} event - The click event object.
 * @param {NavigateFunction} navigate - React Router navigation function
 */
export const handleClickOutsideSource = (
  event: React.MouseEvent<HTMLDivElement>,
  navigate: NavigateFunction
) => {
  // Check if the click target is not within a source element
  if (!(event.target as HTMLElement).closest("[data-source]")) {
    // Create a new URLSearchParams object from the current location's search string
    const searchParams = new URLSearchParams(window.location.search);
    const newParams = new URLSearchParams();

    // Preserve essential parameters
    const paramsToPreserve = [
      "file",
      "page",
      "qaSet",
      "bbox_left",
      "bbox_right",
      "bbox_top",
      "bbox_bottom",
      "bbox_coord_origin"
    ];

    // Copy all preserved parameters
    paramsToPreserve.forEach(param => {
      const value = searchParams.get(param);
      if (value) newParams.set(param, value);
    });

    // Construct the new search string
    const newSearch = newParams.toString();

    // Navigate to the current pathname with the updated search parameters
    // If there are no remaining parameters, navigate without a search string
    navigate(
      newSearch ? `${window.location.pathname}?${newSearch}` : window.location.pathname
    );
  }
};

/**
 * Clears all URL search parameters except essential ones for document navigation
 * and source highlighting.
 *
 * @param searchParams - Current search parameters
 * @param setSearchParams - Function to set new search parameters
 */
export const clearUnwantedSearchParams = (
  searchParams: URLSearchParams,
  setSearchParams: (_searchParams: URLSearchParams) => void
): void => {
  const newParams = new URLSearchParams();

  // List of parameters to preserve
  const paramsToPreserve = [
    "file",
    "page",
  ];

  // Copy all preserved parameters
  paramsToPreserve.forEach(param => {
    const value = searchParams.get(param);
    if (value) newParams.set(param, value);
  });

  setSearchParams(newParams);
};

/**
 * Clears all document-related search parameters (file, page, qaSet, and bbox values).
 *
 * Use when user interactions should NOT trigger the document viewer to open.
 *
 * @param searchParams - Current search parameters
 * @param setSearchParams - Function to set new search parameters
 */
export const clearDocumentSearchParams = (
  searchParams: URLSearchParams,
  setSearchParams: (_searchParams: URLSearchParams) => void
): void => {
  const newParams = new URLSearchParams(searchParams);
  const paramsToRemove = [
    "file",
    "page",
    "qaSet",
    "bbox_left",
    "bbox_right",
    "bbox_top",
    "bbox_bottom",
    "bbox_coord_origin",
  ];
  paramsToRemove.forEach((p) => newParams.delete(p));
  setSearchParams(newParams);
};

/**
 * Type definition for bounding box highlight coordinates
 */
export type BboxHighlight = {
  page: number;
  bbox_left: number;
  bbox_right: number;
  bbox_top: number;
  bbox_bottom: number;
  bbox_coord_origin: string;
};

/**
 * Type definition for page dimensions
 */
export type PageDimensions = {
  width: number;
  height: number;
};

/**
 * Renders a bounding box highlight on a PDF page.
 *
 * This function creates a highlight element based on the provided bounding box coordinates
 * and positions it correctly on the PDF page, taking into account the coordinate system
 * and scaling factors.
 *
 * @param {BboxHighlight | undefined} highlightBbox - The bounding box coordinates to highlight
 * @param {PageDimensions | null} pageDimensions - The dimensions of the PDF page
 * @param {HTMLElement | React.RefObject<HTMLDivElement> | null} containerRef - Reference to the container element
 * @param {Record<number, HTMLDivElement>} pageRefs - References to the page elements
 * @param {HTMLElement | null} bboxHighlightElement - The current highlight element (if any)
 * @param {React.Dispatch<React.SetStateAction<HTMLElement | null>>} setBboxHighlightElement - Function to set the highlight element
 * @returns {void}
 */
export const renderBboxHighlight = (
  highlightBbox: BboxHighlight | undefined,
  pageDimensions: PageDimensions | null,
  containerRef: HTMLElement | React.RefObject<HTMLDivElement> | null,
  pageRefs: React.MutableRefObject<Record<number, HTMLDivElement>>,
  bboxHighlightElement: HTMLElement | null,
  setBboxHighlightElement: React.Dispatch<React.SetStateAction<HTMLElement | null>>
): void => {
  // Remove any existing highlight
  if (bboxHighlightElement) {
    bboxHighlightElement.remove();
    setBboxHighlightElement(null);
  }

  // If no highlight data or required context, exit early without creating a new highlight
  if (!highlightBbox || !pageDimensions || !containerRef) {
    return;
  }

  // Log that we are attempting to render a highlight

  // Find the page element where the highlight should be rendered
  const pageElement = pageRefs.current[highlightBbox.page];
  if (!pageElement) {
    return;
  }

  // Create highlight element
  const highlightEl = document.createElement("div");
  highlightEl.className = "bbox-highlight";
  highlightEl.style.position = "absolute";
  highlightEl.style.backgroundColor = "rgba(255, 0, 0, 0.2)";
  highlightEl.style.border = "2px solid rgba(255, 0, 0, 0.8)";
  highlightEl.style.pointerEvents = "none";
  highlightEl.style.zIndex = "100";
  highlightEl.style.borderRadius = "4px";

  // Calculate position based on bbox coordinates and coordinate origin
  let left: number, top: number, height: number;

  // Get the canvas element within the page
  const canvasElement = pageElement.querySelector(".react-pdf__Page__canvas");
  if (!canvasElement) {
    return;
  }

  const canvasRect = canvasElement.getBoundingClientRect();
  const pageRect = pageElement.getBoundingClientRect();

  // Calculate the scaling factor between original PDF dimensions and rendered dimensions
  const scaleX = canvasRect.width / pageDimensions.width;
  const scaleY = canvasRect.height / pageDimensions.height;

  const width = (highlightBbox.bbox_right - highlightBbox.bbox_left) * scaleX;

  if (highlightBbox.bbox_coord_origin === "BOTTOMLEFT") {
    // Convert from bottom-left origin to top-left origin
    left = highlightBbox.bbox_left * scaleX;
    top = (pageDimensions.height - highlightBbox.bbox_top) * scaleY;
    height = (highlightBbox.bbox_top - highlightBbox.bbox_bottom) * scaleY;
  } else {
    // Assume top-left origin
    left = highlightBbox.bbox_left * scaleX;
    top = highlightBbox.bbox_top * scaleY;
    height = (highlightBbox.bbox_bottom - highlightBbox.bbox_top) * scaleY;
  }

  // Add validation to prevent rendering tiny or invalid highlights
  // Minimum dimensions in pixels
  const MIN_WIDTH = 5;
  const MIN_HEIGHT = 5;

  // Check if the highlight dimensions are too small
  if (width < MIN_WIDTH || height < MIN_HEIGHT) {
    console.log("Highlight too small", { width, height });
    return;
  }

  // Check if the highlight is outside the page boundaries
  if (left < 0 || top < 0 || left > pageDimensions.width * scaleX || top > pageDimensions.height * scaleY) {
    console.log("Highlight outside page boundaries", { left, top });
    return;
  }

  // Position relative to the canvas within the page
  const offsetLeft = canvasRect.left - pageRect.left;
  const offsetTop = canvasRect.top - pageRect.top;

  // Add padding to prevent border from covering text (4px on each side)
  const padding = 4;
  highlightEl.style.left = `${offsetLeft + left - padding}px`;
  highlightEl.style.top = `${offsetTop + top - padding}px`;
  highlightEl.style.width = `${width + (padding * 2)}px`;
  highlightEl.style.height = `${height + (padding * 2)}px`;

  // Add the highlight to the page
  pageElement.style.position = "relative";
  pageElement.appendChild(highlightEl);
  highlightEl.style.zIndex = "10";
  setBboxHighlightElement(highlightEl);
};

/**
 * Handles text selection within a document.
 *
 * This function performs the following steps:
 * 1. Removes the "text-highlight" class from all previously highlighted elements.
 * 2. Clears the list of highlighted elements.
 * 3. Retrieves the current text selection from the window.
 * 4. If there is a text selection, it iterates through the selected ranges and highlights the text
 *    by adding the "text-highlight" class to the parent `SPAN` elements.
 * 5. Updates the state with the newly highlighted elements and the selected text.
 * 6. If no text is selected, it clears the selected text state.
 *
 * @param {HTMLElement[]} highlightedElements - Currently highlighted elements
 * @param {React.Dispatch<React.SetStateAction<HTMLElement[]>>} setHighlightedElements - Function to update highlighted elements
 * @param {React.Dispatch<React.SetStateAction<string | undefined>>} setSelectedText - Function to update selected text
 * @param {URLSearchParams} searchParams - Current URL search parameters
 * @param {(_searchParams: URLSearchParams) => void} setSearchParams - Function to update URL search parameters
 * @param {React.Dispatch<React.SetStateAction<BboxHighlight | undefined>>} setHighlightBbox - Function to update highlight bbox
 * @param {React.Dispatch<React.SetStateAction<HTMLElement | null>>} setBboxHighlightElement - Function to update highlight element
 * @returns {void}
 */
export const handleTextSelection = (
  highlightedElements: HTMLElement[],
  setHighlightedElements: React.Dispatch<React.SetStateAction<HTMLElement[]>>,
  setSelectedText: React.Dispatch<React.SetStateAction<string | undefined>>,
  searchParams: URLSearchParams,
  setSearchParams: (_searchParams: URLSearchParams) => void,
  setHighlightBbox: React.Dispatch<React.SetStateAction<BboxHighlight | undefined>>,
  setBboxHighlightElement: React.Dispatch<React.SetStateAction<HTMLElement | null>>
): void => {
  // Remove highlighting from previous elements
  highlightedElements.forEach((el) => {
    el.classList.remove("text-highlight");
  });
  setHighlightedElements([]);

  // Get current selection
  const selection = window.getSelection();
  if (selection && selection.rangeCount > 0) {
    const text = selection.toString();

    if (text) {
      const selectedElements: HTMLElement[] = [];

      // Process each range in the selection
      for (let i = 0; i < selection.rangeCount; i++) {
        const range = selection.getRangeAt(i);

        // Create a node iterator to find text nodes within the range
        const iterator = document.createNodeIterator(
          range.commonAncestorContainer,
          NodeFilter.SHOW_TEXT,
          {
            acceptNode: function (node) {
              if (range.intersectsNode(node)) {
                return NodeFilter.FILTER_ACCEPT;
              }
              return NodeFilter.FILTER_REJECT;
            },
          }
        );

        // Process each text node
        let currentNode = iterator.nextNode();
        while (currentNode) {
          const parentElement = currentNode.parentElement;
          if (
            parentElement &&
            parentElement.tagName === "SPAN" &&
            !selectedElements.includes(parentElement)
          ) {
            parentElement.classList.add("text-highlight");
            selectedElements.push(parentElement);
          }
          currentNode = iterator.nextNode();
        }
      }

      // Update state with highlighted elements and selected text
      setHighlightedElements(selectedElements);
      setSelectedText(text);

      // Clear unwanted search params and highlight bbox when text is selected
      clearUnwantedSearchParams(searchParams, setSearchParams);
      setHighlightBbox(undefined);
      setBboxHighlightElement(null);
    } else {
      setSelectedText(undefined);
    }
  } else {
    setSelectedText(undefined);
  }
};

/**
 * Groups a flat list of DB chat messages into Q/A pairs.
 * Pairs each user message with the subsequent assistant message; drops incomplete tails.
 */
export function groupDbMessagesIntoQa(messages: AiChatMessage[]): ConversationItem[] {
  if (!Array.isArray(messages) || messages.length === 0) return [];

  const isUser = (type: AiChatMessageType) => type === AiChatMessageType.Question;
  const isAssistant = (type: AiChatMessageType) => type === AiChatMessageType.Answer;

  const pairs: ConversationItem[] = [];
  let pendingQuestion: string | null = null;

  for (const msg of messages) {
    const { text, type } = msg;
    if (!text || !type) continue;

    if (pendingQuestion == null && isUser(type)) {
      pendingQuestion = text;
      continue;
    }

    if (pendingQuestion != null && isAssistant(type)) {
      // Extract sources embedded in assistant text if present
      const parsed = safeParseRawMessage(text);
      pairs.push({
        question: pendingQuestion,
        answer: parsed.answer,
        sources: parsed.sources,
      });
      pendingQuestion = null;
      continue;
    }

    // If we get consecutive users or assistants out of order, try to reset gracefully
    if (isUser(type)) {
      pendingQuestion = text; // start a new pair
    }
  }

  return pairs;
}

/** Safely parse answer + sources from a raw assistant message body. */
export function safeParseRawMessage(raw: string): { answer: string; sources: Source[] } {
  try {
    const { answer, sources } = rawMessageToDisplay(raw ?? "");
    return {
      answer: String(answer ?? ""),
      sources: normalizeSources((sources as unknown as any[]) ?? []),
    };
  } catch {
    return { answer: raw ?? "", sources: [] };
  }
}

/** Normalize mixed source shapes into the `Source` type expected by UI components. */
export function normalizeSources(input: any[]): Source[] {
  if (!Array.isArray(input)) return [];
  const toNum = (v: any): number => {
    const n = typeof v === "string" ? parseFloat(v) : Number(v);
    return Number.isFinite(n) ? n : 0;
  };
  return input
    .map((s) => {
      if (!s) return null;
      let bboxes: any[] | undefined;
      if (s.bboxes != null) {
        if (Array.isArray(s.bboxes)) {
          bboxes = s.bboxes;
        } else if (typeof s.bboxes === "string") {
          try {
            const parsed = JSON.parse(s.bboxes);
            if (Array.isArray(parsed)) bboxes = parsed;
          } catch {
            bboxes = undefined;
          }
        }
      }

      const norm = {
        start_page: toNum(s.start_page ?? s.startPage ?? s.page ?? 0),
        end_page: toNum(s.end_page ?? s.endPage ?? s.page ?? 0),
        source: String(s.source ?? s.document_url ?? s.documentUrl ?? ""),
        bbox_coord_origin: String(s.bbox_coord_origin ?? s.bboxCoordOrigin ?? "TOPLEFT"),
        bboxes: bboxes
          ? bboxes
            .map((b) => ({
              page: toNum(b.page),
              bbox_left: toNum(b.bbox_left ?? b.left),
              bbox_right: toNum(b.bbox_right ?? b.right),
              bbox_top: toNum(b.bbox_top ?? b.top),
              bbox_bottom: toNum(b.bbox_bottom ?? b.bottom),
            }))
            .filter((b) => Number.isFinite(b.page))
          : undefined,
      } as Source;

      if (!norm.source) return null;
      return norm;
    })
    .filter((s): s is Source => s != null);
}
